import { Effects } from './effects.js';

/**
 * WHAT A LOCKED BATTLE SWITCH DOES WHEN IT IS TAPPED.
 *
 * Manga and Pro Storyteller render dark for a player whose plan does not
 * include them, and on a landscape phone the reason line under each pill is
 * visually hidden — the 44px rail has no room for a sentence (see
 * deck-selector.css). So the pill sat at half opacity and swallowed every tap:
 * no motion, no sound, no words. The owner, from the phone: "it's weird rn". A
 * switch that does nothing on a tap reads as BROKEN, not as locked.
 *
 * Three small things, all presentation:
 *   - the thumb carries a padlock, so the rest state says "locked", not "off";
 *   - a tap shakes the pill and plays the refusal thud — `Effects.invalidAction`,
 *     the same beat an unplayable card gives — so the tap is acknowledged as a
 *     tap and answered as a no;
 *   - the reason appears for a moment under the pill where the description is
 *     hidden, and brightens where it is already on screen (the stylesheet
 *     decides which, next to the rule that hides it).
 *
 * Nothing here grants anything. The switch stays inert and reports upward
 * exactly what it reported before; the server's entitlement check is untouched.
 */

const REPLY_VISIBLE_MS = 2600;
const REPLY_EXIT_MS = 220;
const REFUSE_SHAKE_MS = 380;

/* A drawn padlock rather than the 🔒 emoji the catalogs use: the thumb is a
   16px disc inside a half-opacity pill, and a colour emoji at that size is a
   smudge whose look depends on the platform's emoji font. `currentColor` keeps
   it the thumb's own ink. Decorative — `aria-disabled` and the description
   already carry the state. */
const LOCK_GLYPH = '<svg class="battle-switch-lock" viewBox="0 0 12 12" aria-hidden="true" focusable="false">'
    + '<path d="M3.6 5.3V3.9a2.4 2.4 0 0 1 4.8 0v1.4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>'
    + '<rect x="2.3" y="5.3" width="7.4" height="5.2" rx="1.3" fill="currentColor"/>'
    + '</svg>';

/** Put the padlock in the thumb of a switch the account cannot arm. */
export function markSwitchLocked(root, thumb) {
    root.classList.add('is-locked');
    thumb.insertAdjacentHTML('beforeend', LOCK_GLYPH);
}

/* ONE reply on screen, whichever switch was tapped last. The two pills are
   neighbours, so a bubble under each would overlap; and a second tap on the
   same pill restarts the clock rather than stacking a second bubble. */
let current = null;

function dismissReply() {
    if (!current) return;
    clearTimeout(current.exit);
    clearTimeout(current.remove);
    current.el.remove();
    current.root.classList.remove('is-replying');
    current = null;
}

function showReply(root, message) {
    dismissReply();
    const el = document.createElement('span');
    el.className = 'battle-switch-reply';
    /* A live region, so a reader hears the answer to the activation it just
       made; the same sentence also sits behind aria-describedby for focus. */
    el.setAttribute('role', 'status');
    el.textContent = message;
    root.appendChild(el);
    root.classList.add('is-replying');
    const mine = { root, el };
    mine.exit = setTimeout(() => el.classList.add('is-leaving'), REPLY_VISIBLE_MS);
    mine.remove = setTimeout(() => {
        if (current === mine) dismissReply();
    }, REPLY_VISIBLE_MS + REPLY_EXIT_MS);
    current = mine;
}

/**
 * Answer a tap on an inert switch.
 *
 * @param {HTMLElement} root   the control; the reply is positioned from it
 * @param {HTMLElement} toggle the pill, shaken on a refusal
 * @param {{ message: string, refused: boolean }} answer
 *   `refused` — the account asked for something its plan does not include.
 *   A status that is still loading is not a refusal: it gets the words
 *   ("Checking your plan…") without the shake or the thud.
 */
export function answerLockedTap(root, toggle, { message, refused }) {
    if (refused) {
        Effects?.invalidAction?.();
        Effects?.withTempClass?.(toggle, 'is-refused', REFUSE_SHAKE_MS);
    }
    if (message) showReply(root, message);
}
